import { cn } from "@/lib/utils"

function Skeleton({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      className={cn("bg-accent animate-pulse rounded-md", className)}
      {...props}
    />
  )
}

// Pre-built skeleton variants for common use cases
function ImageSkeleton({ className, aspectRatio = "aspect-[3/4]" }: { className?: string; aspectRatio?: string }) {
  return (
    <Skeleton className={cn("w-full", aspectRatio, className)} />
  );
}

function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

function BlogPostSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-4", className)}>
      <Skeleton className="aspect-[16/10] w-full rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

function GallerySkeleton({ count = 6, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid grid-cols-2 md:grid-cols-3 gap-4", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          className="aspect-[3/4] w-full rounded-xl"
          style={{
            animationDelay: `${i * 100}ms`,
          }}
        />
      ))}
    </div>
  );
}

function VideoSkeleton({ className, aspectRatio = "aspect-video" }: { className?: string; aspectRatio?: string }) {
  return (
    <div className={cn("relative overflow-hidden rounded-lg", className)}>
      <Skeleton className={cn("w-full", aspectRatio)} />
      <div className="absolute inset-0 flex items-center justify-center">
        <Skeleton className="h-16 w-16 rounded-full" />
      </div>
    </div>
  );
}

function TableSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 flex-1" />
        </div>
      ))}
    </div>
  );
}

export {
  Skeleton,
  ImageSkeleton,
  CardSkeleton,
  BlogPostSkeleton,
  GallerySkeleton,
  VideoSkeleton,
  TableSkeleton
}
